/**
 * views/MissionPage.tsx
 * ─────────────────────
 * Full mission management page: aisle/location hierarchy selector + list.
 *
 * RBAC: Manager / Admin / Super Admin only.
 * Pilots are shown an Unauthorized message inline.
 *
 * Core component: AisleLocationSelector
 * ───────────────────────────────────────
 * Loads GET /api/manage/warehouses/{id}/aisles → AisleStats[]
 * Builds a shadcn Accordion (one AccordionItem per aisle) with:
 *   • Aisle-level Checkbox  — indeterminate when partially selected
 *   • Per-location Checkbox — individual selection within the accordion body
 *   • "Select All" global toggle
 *   • Live counter: "X / Y locations selected"
 *   • "Verify Selection" → shadcn Dialog listing all selected codes by aisle
 *
 * State model
 * ───────────
 * Selection is a `Set<string>` of location codes for O(1) add/delete/has.
 * The accordion uses Radix primitives directly so a Checkbox can sit to the
 * left of the AccordionTrigger without triggering the expand/collapse.
 *
 * Performance
 * ───────────
 * For typical warehouses (21 aisles × ~50 locations ≈ 1 050 elements) React's
 * reconciler handles a full re-render on each toggle in < 5 ms.  No
 * virtualisation is needed at this scale.  Add react-window if a warehouse
 * exceeds ~200 aisles or ~10 000 total locations.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  ChevronDown,
  Database,
  Check,
  Eye,
  FileText,
  MapPin,
  Plus,
  RefreshCw,
  ShieldAlert,
  User,
} from 'lucide-react';

import { useAuth }      from '@/contexts/AuthContext';
import { useWarehouse } from '@/contexts/WarehouseContext';
import { apiClient, ApiError } from '@/api/client';
import { MissionSohPanel } from '@/components/mission/MissionSohPanel';
import type { AisleStats, Mission, MissionStatus, MissionSummary } from '@/types';

import { Badge }   from '@/components/ui/badge';
import { Button }  from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ALLOWED_ROLES = new Set(['manager', 'admin', 'super_admin']);

const STATUS_VARIANT: Record<MissionStatus, 'default' | 'success' | 'warning' | 'muted'> = {
  PENDING:   'warning',
  ACTIVE:    'success',
  COMPLETED: 'default',
  CANCELLED: 'muted',
};

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

interface PilotUser { id: string; name: string; email: string; }

// ── AisleLocationSelector types ───────────────────────────────────────────────

type SelectionSet = ReadonlySet<string>;

interface AisleState {
  /** How many locations in this aisle are currently selected. */
  count: number;
  /** Radix-compatible checkbox state for the aisle header. */
  checkState: boolean | 'indeterminate';
}

function deriveAisleState(aisle: AisleStats, selected: SelectionSet): AisleState {
  const count = aisle.locations.reduce((n, loc) => n + (selected.has(loc) ? 1 : 0), 0);
  const checkState: boolean | 'indeterminate' =
    count === 0                     ? false :
    count === aisle.location_count  ? true  :
    'indeterminate';
  return { count, checkState };
}

// ── VerifyDialog ──────────────────────────────────────────────────────────────

function VerifyDialog({
  open,
  onClose,
  aisleData,
  selected,
  pilotAssignments,
  pilots,
}: {
  open: boolean;
  onClose: () => void;
  aisleData: AisleStats[];
  selected: SelectionSet;
  pilotAssignments?: Record<string, string>;
  pilots?: PilotUser[];
}) {
  const pilotName = (id: string) => pilots?.find(p => p.id === id)?.name ?? id;
  const populated = aisleData.filter(a => a.locations.some(l => selected.has(l)));

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl bg-white max-h-[80vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            {selected.size.toLocaleString()} Location
            {selected.size !== 1 ? 's' : ''} Selected
            {' '}across {populated.length} Aisle
            {populated.length !== 1 ? 's' : ''}
          </DialogTitle>
        </DialogHeader>

        {populated.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No locations selected yet.
          </p>
        ) : (
          <div className="overflow-y-auto space-y-5 pr-1 mt-2">
            {populated.map(aisle => {
              const selLocs = aisle.locations.filter(l => selected.has(l));
              return (
                <div key={aisle.aisle}>
                  <div className="mb-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Aisle {aisle.aisle}
                    </span>
                    <Badge variant="muted" className="text-xs">
                      {selLocs.length} / {aisle.location_count}
                    </Badge>
                    {pilotAssignments?.[aisle.aisle] && (
                      <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                        <User className="h-3 w-3" />
                        {pilotName(pilotAssignments[aisle.aisle])}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selLocs.map(loc => (
                      <code
                        key={loc}
                        className="rounded bg-primary/8 px-1.5 py-0.5 text-xs font-mono text-primary"
                      >
                        {loc}
                      </code>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="shrink-0 pt-3 border-t mt-3">
          <Button variant="outline" className="w-full" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── LocationRow ───────────────────────────────────────────────────────────────
//
// Memoised.  Props compared shallowly — only re-renders when `checked` flips.

const LocationRow = React.memo(function LocationRow({
  location,
  checked,
  onToggle,
  disabled,
}: {
  location: string;
  checked: boolean;
  onToggle: (loc: string) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`
        flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer select-none
        transition-colors
        ${checked ? 'bg-primary/6' : 'hover:bg-muted/60'}
        ${disabled ? 'opacity-50 pointer-events-none' : ''}
      `}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={() => onToggle(location)}
        disabled={disabled}
      />
      <code className="text-xs font-mono text-muted-foreground leading-none">
        {location}
      </code>
    </label>
  );
});

// ── AislePanel ────────────────────────────────────────────────────────────────
//
// One AccordionItem.  Uses Radix primitives directly so the aisle checkbox
// sits BESIDE (not inside) the AccordionTrigger, with stopPropagation
// keeping the checkbox click from toggling the accordion.

const AislePanel = React.memo(function AislePanel({
  aisle,
  aisleState,
  selected,
  onToggleAisle,
  onToggleLocation,
  disabled,
  pilots,
  assignedPilotId,
  onPilotAssign,
}: {
  aisle:            AisleStats;
  aisleState:       AisleState;
  selected:         SelectionSet;
  onToggleAisle:    (aisle: AisleStats) => void;
  onToggleLocation: (loc: string) => void;
  disabled?:        boolean;
  pilots?:          PilotUser[];
  assignedPilotId?: string;
  onPilotAssign?:   (aisle: string, pilotId: string) => void;
}) {
  const stopProp = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();

  const { count, checkState } = aisleState;

  return (
    <AccordionPrimitive.Item
      value={aisle.aisle}
      className="border-b last:border-b-0"
    >
      {/* ── Custom header: [Checkbox] [AisleName + Counter] [Chevron] ── */}
      <AccordionPrimitive.Header className="flex items-stretch">
        {/*
          The checkbox div must NOT propagate clicks to the Accordion trigger.
          Both onClick and onKeyDown are suppressed so keyboard navigation
          (Space/Enter on the checkbox) also doesn't expand the accordion.
        */}
        <div
          className="flex items-center px-3 py-3 shrink-0"
          onClick={stopProp}
          onKeyDown={stopProp}
        >
          <Checkbox
            checked={checkState}
            onCheckedChange={() => onToggleAisle(aisle)}
            disabled={disabled}
            aria-label={`Select all locations in aisle ${aisle.aisle}`}
          />
        </div>

        {/*
          The trigger is the remaining row.  Clicking it expands / collapses
          the location list.  The data-[state=open] sibling selector on the
          ChevronDown icon provides the rotation animation.
        */}
        <AccordionPrimitive.Trigger
          className="
            flex flex-1 items-center justify-between
            py-3 pr-4
            text-sm font-medium
            transition-colors
            hover:bg-muted/30
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset
            [&[data-state=open]_svg.chevron]:rotate-180
          "
        >
          <span className="tabular-nums">
            Aisle{' '}
            <span className="font-semibold">{aisle.aisle}</span>
          </span>

          <div className="flex items-center gap-2.5 ml-auto mr-2">
            {/* Selection counter badge */}
            <span
              className={`
                text-xs tabular-nums px-1.5 py-0.5 rounded-full font-medium
                ${count > 0
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground'}
              `}
            >
              {count} / {aisle.location_count}
            </span>

            <ChevronDown className="chevron h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>

      {/* ── Location grid ── */}
      <AccordionPrimitive.Content className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <div className="px-3 pb-3 pt-1">
          {/* Per-aisle "select all" shortcut */}
          <div className="mb-2 flex items-center justify-between">
            <button
              type="button"
              className="text-xs text-primary hover:underline disabled:opacity-50"
              disabled={disabled}
              onClick={() => onToggleAisle(aisle)}
            >
              {checkState === true ? 'Deselect aisle' : 'Select entire aisle'}
            </button>
            <span className="text-xs text-muted-foreground">
              {aisle.location_count} location{aisle.location_count !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Location checkboxes — grid adapts to column count */}
          <div className="grid grid-cols-2 gap-0.5 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {aisle.locations.map(loc => (
              <LocationRow
                key={loc}
                location={loc}
                checked={selected.has(loc)}
                onToggle={onToggleLocation}
                disabled={disabled}
              />
            ))}
          </div>

          {/* Per-aisle pilot assignment — visible only when at least one location is selected */}
          {count > 0 && pilots && pilots.length > 0 && (
            <div className="mt-3 flex items-center gap-2 border-t pt-3">
              <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Pilot for Aisle {aisle.aisle}:
              </span>
              <select
                className="flex-1 min-w-0 rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                value={assignedPilotId ?? ''}
                onChange={e => onPilotAssign?.(aisle.aisle, e.target.value)}
                disabled={disabled}
              >
                <option value="">— Use mission default —</option>
                {pilots.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AccordionPrimitive.Content>
    </AccordionPrimitive.Item>
  );
});

// ── AisleLocationSelector ─────────────────────────────────────────────────────

function AisleLocationSelector({
  aisleData,
  loading,
  value,
  onChange,
  disabled,
  pilots,
  pilotAssignments,
  onPilotAssign,
}: {
  aisleData: AisleStats[];
  loading: boolean;
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
  pilots?: PilotUser[];
  pilotAssignments?: Record<string, string>;
  onPilotAssign?: (aisle: string, pilotId: string) => void;
}) {
  const [verifyOpen, setVerifyOpen] = useState(false);

  // ── Derived totals ──────────────────────────────────────────────────────────
  const totalLocations = useMemo(
    () => aisleData.reduce((s, a) => s + a.location_count, 0),
    [aisleData],
  );

  const globalCheckState = useMemo((): boolean | 'indeterminate' => {
    if (value.size === 0)                          return false;
    if (value.size === totalLocations)             return true;
    return 'indeterminate';
  }, [value.size, totalLocations]);

  // Per-aisle derived state — recomputed when selection changes
  const aisleStates = useMemo<Map<string, AisleState>>(() => {
    const map = new Map<string, AisleState>();
    for (const aisle of aisleData) {
      map.set(aisle.aisle, deriveAisleState(aisle, value));
    }
    return map;
  }, [aisleData, value]);

  // ── Stable callbacks ────────────────────────────────────────────────────────

  const handleToggleAll = useCallback(() => {
    if (globalCheckState === true) {
      onChange(new Set());
    } else {
      const next = new Set<string>();
      aisleData.forEach(a => a.locations.forEach(l => next.add(l)));
      onChange(next);
    }
  }, [globalCheckState, aisleData, onChange]);

  // `value` is a controlled prop so it's always current — use it directly.
  const handleToggleAisle = useCallback((aisle: AisleStats) => {
    const next = new Set(value);
    const { checkState } = deriveAisleState(aisle, value);
    if (checkState === true) {
      aisle.locations.forEach(l => next.delete(l));
    } else {
      // unchecked OR indeterminate → select all locations in this aisle
      aisle.locations.forEach(l => next.add(l));
    }
    onChange(next);
  }, [value, onChange]);

  const handleToggleLocation = useCallback((loc: string) => {
    const next = new Set(value);
    if (next.has(loc)) next.delete(loc);
    else next.add(loc);
    onChange(next);
  }, [value, onChange]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading aisles…
      </div>
    );
  }

  if (aisleData.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground italic">
        No location master uploaded for this warehouse.
        Upload one in the Warehouses section first.
      </p>
    );
  }

  return (
    <div className="space-y-3">

      {/* ── Top bar: select-all + live counter + verify button ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">

        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <Checkbox
            checked={globalCheckState}
            onCheckedChange={handleToggleAll}
            disabled={disabled}
            aria-label="Select all aisles"
          />
          <span className="text-sm font-medium">
            {globalCheckState === true
              ? `Deselect all (${aisleData.length} aisles)`
              : `Select all aisles (${aisleData.length})`}
          </span>
        </label>

        <div className="flex items-center gap-3">
          {/* Live counter */}
          <div
            className={`
              flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ring-1
              ${value.size > 0
                ? 'bg-primary/8 text-primary ring-primary/20'
                : 'bg-muted text-muted-foreground ring-transparent'}
            `}
            title="Locations selected"
          >
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="tabular-nums">{value.size.toLocaleString()}</span>
            <span className="font-normal text-xs opacity-70">
              / {totalLocations.toLocaleString()} locations
            </span>
          </div>

          {/* Verify button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={value.size === 0}
            onClick={() => setVerifyOpen(true)}
            className="gap-1.5"
          >
            <Eye className="h-3.5 w-3.5" />
            Verify Selection
          </Button>
        </div>
      </div>

      {/* ── Accordion ── */}
      <div className="rounded-lg border overflow-hidden bg-white">
        <AccordionPrimitive.Root type="multiple">
          {aisleData.map(aisle => (
            <AislePanel
              key={aisle.aisle}
              aisle={aisle}
              aisleState={aisleStates.get(aisle.aisle)!}
              selected={value}
              onToggleAisle={handleToggleAisle}
              onToggleLocation={handleToggleLocation}
              disabled={disabled}
              pilots={pilots}
              assignedPilotId={pilotAssignments?.[aisle.aisle]}
              onPilotAssign={onPilotAssign}
            />
          ))}
        </AccordionPrimitive.Root>
      </div>

      {/* ── Verification dialog ── */}
      <VerifyDialog
        open={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        aisleData={aisleData}
        selected={value}
        pilotAssignments={pilotAssignments}
        pilots={pilots}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CREATE MISSION FORM
// ══════════════════════════════════════════════════════════════════════════════

function CreateMissionForm({ onCreated }: { onCreated: () => void }) {
  const { warehouses } = useWarehouse();

  // Form fields
  const [title,       setTitle]       = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  // Many-to-many: several pilots share one mission (and its single report).
  const [pilotIds,    setPilotIds]    = useState<Set<string>>(new Set());
  const [startDate,   setStartDate]   = useState('');
  const [endDate,     setEndDate]     = useState('');
  const [notes,       setNotes]       = useState('');

  // Aisle / location selection (shared scope — all assigned pilots share the aisles)
  const [aisleData,     setAisleData]     = useState<AisleStats[]>([]);
  const [aislesLoading, setAislesLoading] = useState(false);
  const [selectedLocs,  setSelectedLocs]  = useState<Set<string>>(new Set());

  // Pilot list
  const [pilots, setPilots] = useState<PilotUser[]>([]);

  // Mission SOH snapshot (0044: uploaded against the new mission)
  const [sohFile, setSohFile] = useState<File | null>(null);
  const sohFileRef = React.useRef<HTMLInputElement>(null);

  // Submission
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load pilots once
  useEffect(() => {
    apiClient.get<{ id: string; name: string; email: string; role: string }[]>('/api/auth/users')
      .then(u => setPilots(u.filter(x => x.role === 'pilot')))
      .catch(() => {});
  }, []);

  // Reload aisle data when warehouse changes
  useEffect(() => {
    if (!warehouseId) { setAisleData([]); setSelectedLocs(new Set()); return; }
    setAislesLoading(true);
    setSelectedLocs(new Set());
    apiClient.get<AisleStats[]>(`/api/manage/warehouses/${warehouseId}/aisles`)
      .then(setAisleData)
      .catch(() => setAisleData([]))
      .finally(() => setAislesLoading(false));
  }, [warehouseId]);

  // Derive which aisles have at least 1 selected location → sent to the API
  const allocatedAisles = useMemo(() => {
    return aisleData
      .filter(a => a.locations.some(l => selectedLocs.has(l)))
      .map(a => a.aisle);
  }, [aisleData, selectedLocs]);

  // The onChange callback is kept stable so it doesn't re-mount AisleLocationSelector
  const handleSelectionChange = useCallback((next: Set<string>) => {
    setSelectedLocs(next);
  }, []);

  const togglePilot = useCallback((id: string) => {
    setPilotIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setSuccess(null);
    try {
      const mission = await apiClient.post<Mission & { location_count?: number }>('/api/missions', {
        title: title.trim(),
        warehouse_id: warehouseId,
        pilot_ids: [...pilotIds],
        allocated_aisles: allocatedAisles,
        scheduled_start: startDate || null,
        scheduled_end:   endDate   || null,
        notes: notes.trim() || null,
      });
      const locMsg = mission.location_count
        ? ` — ${mission.location_count.toLocaleString()} locations assigned`
        : '';

      // Upload the SOH snapshot against the new mission (photos + SOH are both
      // point-in-time captures of the audit's inventory).
      let sohMsg = '.';
      if (sohFile) {
        try {
          const fd = new FormData();
          fd.append('file', sohFile);
          fd.append('mission_id', mission.id);
          const soh = await apiClient.postForm('/api/manage/upload-soh', fd) as
            { rows_inserted: number; rows_rejected: number };
          sohMsg = ` — SOH loaded (${soh.rows_inserted.toLocaleString()} rows` +
            (soh.rows_rejected > 0 ? `, ${soh.rows_rejected.toLocaleString()} rejected` : '') + ').';
        } catch (sohErr) {
          sohMsg = `. SOH upload failed: ${
            sohErr instanceof ApiError ? sohErr.message : 'unknown error'
          } — upload it from the mission list (SOH button).`;
        }
      }

      setSuccess(`Mission "${mission.title}" created${locMsg}${sohMsg}`);
      // Reset form
      setTitle(''); setPilotIds(new Set()); setStartDate(''); setEndDate(''); setNotes('');
      setSelectedLocs(new Set());
      setSohFile(null);
      if (sohFileRef.current) sohFileRef.current.value = '';
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create mission.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl space-y-6">

      {/* Title */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">
          Mission title <span className="text-destructive">*</span>
        </label>
        <input
          className="form-input w-full"
          placeholder="e.g. Week 21 — Aisles A–D"
          value={title}
          onChange={e => setTitle(e.target.value)}
          required disabled={busy}
        />
      </div>

      {/* Warehouse + Pilot */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Warehouse <span className="text-destructive">*</span>
          </label>
          <select
            className="form-input w-full"
            value={warehouseId}
            onChange={e => setWarehouseId(e.target.value)}
            required disabled={busy}
          >
            <option value="">— Select warehouse —</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <label className="flex items-center gap-1.5 text-sm font-medium">
            <User className="h-3.5 w-3.5" />
            Assign pilots
            {pilotIds.size > 0 && (
              <span className="text-xs font-normal text-muted-foreground">
                ({pilotIds.size} selected)
              </span>
            )}
          </label>
          <div className="form-input w-full min-h-[2.5rem] flex flex-wrap gap-1.5 py-1.5">
            {pilots.length === 0 ? (
              <span className="self-center text-sm italic text-muted-foreground/70">
                No pilots available
              </span>
            ) : (
              pilots.map(p => {
                const on = pilotIds.has(p.id);
                return (
                  <button
                    type="button"
                    key={p.id}
                    disabled={busy}
                    title={p.email}
                    onClick={() => togglePilot(p.id)}
                    className={
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-sm transition ' +
                      (on
                        ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-input text-muted-foreground hover:border-primary/50')
                    }
                  >
                    {on && <Check className="h-3 w-3" />}
                    {p.name}
                  </button>
                );
              })
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {pilotIds.size === 0
              ? 'Unassigned — any pilot of the warehouse can fly it.'
              : `${pilotIds.size} pilot${pilotIds.size > 1 ? 's' : ''} share this mission and its single report.`}
          </p>
        </div>
      </div>

      {/* ── Aisle / Location selector ── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">
            Allocated Aisles &amp; Locations
          </label>
          {allocatedAisles.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {allocatedAisles.length} aisle{allocatedAisles.length !== 1 ? 's' : ''} will be assigned
            </span>
          )}
        </div>

        {!warehouseId ? (
          <div className="rounded-lg border border-dashed px-4 py-8 text-center">
            <MapPin className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Select a warehouse above to load its aisle and location master.
            </p>
          </div>
        ) : (
          <AisleLocationSelector
            aisleData={aisleData}
            loading={aislesLoading}
            value={selectedLocs}
            onChange={handleSelectionChange}
            disabled={busy}
          />
        )}
      </div>

      {/* Schedule */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Scheduled start</label>
          <input
            className="form-input w-full"
            type="datetime-local"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Scheduled end</label>
          <input
            className="form-input w-full"
            type="datetime-local"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>

      {/* Mission SOH snapshot */}
      <div className="space-y-1.5">
        <label className="flex items-center gap-1.5 text-sm font-medium">
          <Database className="h-3.5 w-3.5" />
          Stock-on-Hand snapshot (optional)
        </label>
        <input
          ref={sohFileRef}
          className="form-input w-full"
          type="file"
          accept=".xlsx,.xls,.xlsm,.csv"
          onChange={e => setSohFile(e.target.files?.[0] ?? null)}
          disabled={busy}
        />
        <p className="text-xs text-muted-foreground">
          The WMS export this audit will be reconciled against. You can also
          upload it later from the mission list (SOH button).
        </p>
      </div>

      {/* Notes */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Notes (optional)</label>
        <input
          className="form-input w-full"
          placeholder="Any briefing notes for the pilot"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          disabled={busy}
        />
      </div>

      {error   && <p className="text-sm text-destructive">{error}</p>}
      {success && <p className="text-sm text-green-700">{success}</p>}

      <Button
        type="submit"
        disabled={busy || !title.trim() || !warehouseId}
        className="gap-2 w-full sm:w-auto"
      >
        <Plus className="h-4 w-4" />
        {busy ? 'Creating…' : '+ Create Mission'}
      </Button>
    </form>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// EDIT PILOTS DIALOG — reassign the pilots on an existing mission
// ══════════════════════════════════════════════════════════════════════════════

function EditPilotsDialog({ mission, pilots, onClose, onSaved }: {
  mission: Mission;
  pilots: PilotUser[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = mission.pilots && mission.pilots.length > 0
    ? mission.pilots.map(p => p.id)
    : (mission.pilot_id ? [mission.pilot_id] : []);
  const [sel, setSel]     = useState<Set<string>>(new Set(initial));
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => setSel(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = async () => {
    setBusy(true); setError(null);
    try {
      await apiClient.patch(`/api/missions/${mission.id}`, { pilot_ids: [...sel] });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update pilots.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Assign pilots — {mission.title ?? mission.id.slice(0, 8)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {pilots.length === 0 ? (
              <span className="text-sm italic text-muted-foreground/70">No pilots available</span>
            ) : pilots.map(p => {
              const on = sel.has(p.id);
              return (
                <button
                  type="button" key={p.id} disabled={busy} title={p.email}
                  onClick={() => toggle(p.id)}
                  className={
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-sm transition ' +
                    (on ? 'border-primary bg-primary/10 text-primary font-medium'
                        : 'border-input text-muted-foreground hover:border-primary/50')
                  }
                >
                  {on && <Check className="h-3 w-3" />}{p.name}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {sel.size === 0
              ? 'Unassigned — any pilot of the warehouse can fly it.'
              : `${sel.size} pilot${sel.size > 1 ? 's' : ''} share this mission and its single report.`}
          </p>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save pilots'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MISSION LIST TAB
// ══════════════════════════════════════════════════════════════════════════════

function MissionListTab({ missions, scanSummaries, onStatusChange, canManage }: {
  missions: Mission[];
  scanSummaries: MissionSummary[] | null;
  onStatusChange: () => void;
  canManage: boolean;
}) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reconcileMsg, setReconcileMsg] = useState<Record<string, string>>({});
  // Mission whose SOH dialog is open (0044: SOH is uploaded per mission).
  const [sohMission, setSohMission] = useState<Mission | null>(null);
  // Mission whose pilot-assignment dialog is open.
  const [editMission, setEditMission] = useState<Mission | null>(null);
  const [pilots, setPilots] = useState<PilotUser[]>([]);
  useEffect(() => {
    apiClient.get<{ id: string; name: string; email: string; role: string }[]>('/api/auth/users')
      .then(u => setPilots(u.filter(x => x.role === 'pilot')))
      .catch(() => {});
  }, []);

  const setStatus = async (id: string, status: string) => {
    setBusyId(id);
    try {
      await apiClient.patch(`/api/missions/${id}`, { status });
      onStatusChange();
    } catch { /* silently ignore */ } finally {
      setBusyId(null);
    }
  };

  // Manager/admin: (re)generate the report by running reconciliation. Requires an
  // active SOH snapshot — the backend timing gate returns a 409 with guidance if
  // none is uploaded yet, which we surface inline per-row.
  const runReconcile = async (id: string) => {
    setBusyId(id);
    setReconcileMsg(prev => ({ ...prev, [id]: '' }));
    try {
      const res = await apiClient.post<{ records_written: number; message?: string }>(
        `/api/missions/${id}/reconcile`,
        {},
      );
      setReconcileMsg(prev => ({
        ...prev,
        [id]: res.message || `Report processed — ${res.records_written.toLocaleString()} rows written.`,
      }));
      onStatusChange();
    } catch (e) {
      setReconcileMsg(prev => ({
        ...prev,
        [id]: e instanceof ApiError ? e.message : 'Reconcile failed.',
      }));
    } finally {
      setBusyId(null);
    }
  };

  if (missions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <MapPin className="mb-3 h-10 w-10 text-muted-foreground/40" />
        <p className="text-sm font-medium">No missions yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Click "+ New Mission" to create the first one.
        </p>
      </div>
    );
  }

  const active   = missions.filter(m => m.status !== 'CANCELLED');
  const archived = missions.filter(m => m.status === 'CANCELLED');

  const renderRows = (rows: Mission[]) => rows.map(m => (
    <TableRow key={m.id}>
      <TableCell>
        <Badge variant={STATUS_VARIANT[m.status]} className="text-xs">
          {m.status}
        </Badge>
      </TableCell>
      <TableCell className="font-medium max-w-[200px] truncate">
        {m.title ?? <code className="text-xs text-muted-foreground">{m.id.slice(0,8)}…</code>}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {m.warehouse_name ?? '—'}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground align-top max-w-[180px]">
        {m.pilots && m.pilots.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {m.pilots.map(p => (
              <span
                key={p.id}
                title={p.email ?? undefined}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
              >
                <User className="h-3 w-3" />
                {p.name ?? p.email ?? p.id.slice(0, 8)}
              </span>
            ))}
          </div>
        ) : (
          m.pilot_name ?? <span className="italic opacity-60">Unassigned</span>
        )}
      </TableCell>
      <TableCell>
        {m.allocated_aisles.length === 0 ? (
          <span className="text-xs text-muted-foreground/60 italic">All aisles</span>
        ) : (
          <div className="space-y-1">
            <span className="text-xs font-medium text-muted-foreground">
              Total Aisles: {m.allocated_aisles.length}
            </span>
            <div className="space-y-0.5">
              {Array.from(
                { length: Math.ceil(m.allocated_aisles.length / 10) },
                (_, i) => [...m.allocated_aisles].sort().slice(i * 10, i * 10 + 10),
              ).map((row, i) => (
                <div key={i} className="flex gap-1">
                  {row.map(a => (
                    <code key={a} className="rounded bg-muted px-1 py-0.5 text-xs">{a}</code>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {fmtDate(m.scheduled_start)}
      </TableCell>
      <TableCell className="align-top">
        <div className="flex flex-wrap items-center gap-1 max-w-[300px]">
          {canManage && m.status === 'PENDING' && (
            <Button size="sm" className="h-7 px-2 text-xs" onClick={() => setStatus(m.id, 'ACTIVE')} disabled={busyId === m.id}>
              Activate
            </Button>
          )}
          {canManage && m.status === 'ACTIVE' && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setStatus(m.id, 'COMPLETED')} disabled={busyId === m.id}>
              Complete
            </Button>
          )}
          {canManage && (m.status === 'PENDING' || m.status === 'ACTIVE') && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-destructive hover:text-destructive" onClick={() => setStatus(m.id, 'CANCELLED')} disabled={busyId === m.id}>
              ✕
            </Button>
          )}
          {/* Reassign pilots — many-to-many; one report shared by all assigned pilots */}
          {canManage && m.status !== 'CANCELLED' && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1"
              title="Assign or reassign the pilots on this mission"
              onClick={() => setEditMission(m)}
            >
              <User className="h-3 w-3" />
              Pilots
            </Button>
          )}
          {/* Mission SOH — every audit pairs its photos with its own WMS snapshot */}
          {m.warehouse_id && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1"
              title="View or upload the WMS Stock-on-Hand snapshot for this mission"
              onClick={() => setSohMission(m)}
            >
              <Database className="h-3 w-3" />
              SOH
            </Button>
          )}
          {/* Exception triage — always available so a manager can assign unmatched photos */}
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-xs"
            title="Open exception triage for this mission"
            onClick={() => navigate(`/dashboard/triage/${m.id}`)}
          >
            🔍 Triage
          </Button>
          {/* Process Report — manager/admin: run reconciliation to (re)generate the
              report once the SOH is uploaded. Backend timing-gate 409s (surfaced
              inline below) if no active SOH exists yet. */}
          {canManage && m.status !== 'CANCELLED' && (
            <Button
              size="sm"
              className="h-7 px-2 text-xs gap-1"
              title="Run reconciliation to (re)generate the report — requires an uploaded SOH"
              onClick={() => runReconcile(m.id)}
              disabled={busyId === m.id}
            >
              <RefreshCw className={`h-3 w-3 ${busyId === m.id ? 'animate-spin' : ''}`} />
              {busyId === m.id ? 'Processing…' : 'Process Report'}
            </Button>
          )}
          {/* Generated report — shown once the mission has scan data */}
          {(scanSummaries?.find(s => s.mission_id === m.id)?.record_count ?? 0) > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs gap-1"
              title="View the generated inventory report for this mission"
              onClick={() => navigate(`/dashboard/inventory?mission=${m.id}`)}
            >
              <FileText className="h-3 w-3" />
              Report
            </Button>
          )}
        </div>
        {reconcileMsg[m.id] && (
          <p className="mt-1 max-w-[300px] text-[11px] leading-tight text-muted-foreground">
            {reconcileMsg[m.id]}
          </p>
        )}
      </TableCell>
    </TableRow>
  ));

  return (
    <div className="space-y-4">
      {/* Mission SOH dialog */}
      <Dialog open={sohMission !== null} onOpenChange={open => { if (!open) setSohMission(null); }}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle>
              Stock-on-Hand — {sohMission?.title ?? sohMission?.id.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          {sohMission?.warehouse_id && (
            <MissionSohPanel
              missionId={sohMission.id}
              warehouseId={sohMission.warehouse_id}
              frozen={!!sohMission.report_frozen_at}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Reassign-pilots dialog */}
      {editMission && (
        <EditPilotsDialog
          mission={editMission}
          pilots={pilots}
          onClose={() => setEditMission(null)}
          onSaved={onStatusChange}
        />
      )}

      {/* Active missions */}
      {active.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center rounded-lg border bg-white">
          <MapPin className="mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm font-medium">No active missions</p>
          <p className="mt-1 text-xs text-muted-foreground">Click "+ New Mission" to create one.</p>
        </div>
      ) : (
        <div className="rounded-lg border bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Status</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead>Pilot</TableHead>
                <TableHead>Aisles</TableHead>
                <TableHead>Start</TableHead>
                <TableHead className="min-w-[15rem]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{renderRows(active)}</TableBody>
          </Table>
        </div>
      )}

      {/* Archived (cancelled) missions — collapsible */}
      {archived.length > 0 && (
        <details className="group">
          <summary className="flex items-center gap-2 cursor-pointer select-none text-sm text-muted-foreground hover:text-foreground py-2 px-1 rounded-md hover:bg-muted/50 transition-colors w-fit">
            <span className="group-open:rotate-90 transition-transform inline-block">▶</span>
            <span>Archived missions ({archived.length})</span>
          </summary>
          <div className="mt-2 rounded-lg border bg-white overflow-x-auto opacity-60">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Pilot</TableHead>
                  <TableHead>Aisles</TableHead>
                  <TableHead>Start</TableHead>
                  <TableHead className="min-w-[15rem]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>{renderRows(archived)}</TableBody>
            </Table>
          </div>
        </details>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MISSION PAGE  (RBAC-gated entry point)
// ══════════════════════════════════════════════════════════════════════════════

export default function MissionPage() {
  const { user } = useAuth();
  const { activeWarehouseId } = useWarehouse();

  const [tab,      setTab]      = useState<'list' | 'create'>('list');
  const [missions, setMissions] = useState<Mission[] | null>(null);
  const [scanSummaries, setScanSummaries] = useState<MissionSummary[] | null>(null);
  const [error,    setError]    = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);

  // Derived once — used by both the guard and the callback.
  const isAllowed = Boolean(user && ALLOWED_ROLES.has(user.role));
  // Managers may only triage; mission management (create / activate / complete /
  // cancel) is restricted to admin + super_admin.
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // ── Data loading ──────────────────────────────────────────────────────────
  // Hooks MUST all be declared before any conditional return (Rules of Hooks).
  // The guard inside the callback makes it a no-op for unauthorised users.

  const loadMissions = useCallback(async () => {
    if (!isAllowed) return;
    setLoading(true);
    const params = activeWarehouseId ? `?warehouse_id=${activeWarehouseId}` : '';
    try {
      const data = await apiClient.get<Mission[]>(`/api/missions${params}`);
      setMissions(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load missions.');
    } finally {
      setLoading(false);
    }
    // Scan-data summaries power the per-mission Report action — non-fatal.
    try {
      const sums = await apiClient.get<MissionSummary[]>(`/api/inventory/missions${params}`);
      setScanSummaries(sums);
    } catch { /* non-fatal */ }
  }, [activeWarehouseId, isAllowed]);

  useEffect(() => { loadMissions(); }, [loadMissions]);

  // ── RBAC guard (after all hooks) ──────────────────────────────────────────
  if (!isAllowed) {
    return (
      <Card className="border-destructive/40 bg-destructive/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-5 w-5" />
            Access Restricted
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Mission management requires <strong>Manager</strong>,{' '}
            <strong>Admin</strong>, or <strong>Super Admin</strong> privileges.
          </p>
        </CardContent>
      </Card>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Tab bar */}
      <div className="flex items-center gap-1 border-b pb-0">

        {/* Mission List tab */}
        <button
          type="button"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'list'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setTab('list')}
        >
          Mission List
          {missions !== null && (
            <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-xs">
              {missions.length}
            </span>
          )}
        </button>

        {/* New Mission tab — admin only; managers triage, they don't create */}
        {isAdmin && (
        <button
          type="button"
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'create'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setTab('create')}
        >
          + New Mission
        </button>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 gap-1.5 px-2 text-xs"
          onClick={loadMissions}
          disabled={loading}
        >
          <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Tab: Create (admin only) */}
      {isAdmin && tab === 'create' && (
        <CreateMissionForm
          onCreated={() => { loadMissions(); setTab('list'); }}
        />
      )}

      {/* Tab: List */}
      {tab === 'list' && (
        <>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          {loading && !missions && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading missions…
            </div>
          )}
          {missions !== null && (
            <MissionListTab missions={missions} scanSummaries={scanSummaries} onStatusChange={loadMissions} canManage={isAdmin} />
          )}
        </>
      )}
    </div>
  );
}
